/* eslint-disable @typescript-eslint/naming-convention */
import { Location } from "@angular/common";
import { Component, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize } from "rxjs/operators";
import { Operation } from "src/app/shared/types/entity.type";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { QueryParams } from "../../../../core/http/query-params.type";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DataTableComponent } from "../../../../shared/components/data-table/data-table.component";
import { DataTable, GetDataEvent } from "../../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../../shared/components/delete-modal/delete-modal.component";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { GenericModalComponent } from "../../../../shared/components/generic-modal/generic-modal.component";
import { GenericModalData } from "../../../../shared/components/generic-modal/generic-modal.type";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { ViewAsService } from "../../../../shared/components/view-as/view-as.service";
import { UserInfo } from "../../../../shared/types/user-info";
import { getUpdatedValues } from "../../../../shared/utils/helpers/common.helper";
import { allowDeleteEntity, allowUpdateEntity } from "../../../../shared/utils/helpers/permission.helper";
import { CompanyTableService } from "../../companies/company-table.service";
import { CompanyService } from "../../companies/company.service";
import { Company } from "../../companies/company.type";
import { CreateCompanyService } from "../../companies/create-company/create-company.service";
import { UserTableService } from "../../users/user-table.service";
import { UserService } from "../../users/user.service";
import { User } from "../../users/user.type";
import { CreatePartnerService } from "../create-partner/create-partner.service";
import { PartnerService } from "../partner.service";
import { Partner } from "../partner.type";

@Component({
  selector: "app-manage-partner",
  templateUrl: "./manage-partner.component.html",
  styleUrls: ["./manage-partner.component.scss"],
  providers: [CreateCompanyService, CreatePartnerService],
})
export class ManagePartnerComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;
  @ViewChildren(DataTableComponent) tables: QueryList<DataTableComponent<unknown>>;

  currentUser: UserInfo;
  questions: QuestionBase<string | boolean>[];
  isEdit: boolean;
  partnerId: string;
  partner: Partner;
  dialogRef: MatDialogRef<GenericModalComponent>;
  isEveryTabCreated: boolean;
  companiesTableConfig: DataTable<Company>;
  usersTableConfig: DataTable<User>;
  cwRepsTableConfig: DataTable<User>;
  operation: Operation;

  constructor(
    public dialog: MatDialog,
    public location: Location,
    public createCompanyService: CreateCompanyService,
    public companyTableService: CompanyTableService,
    public userTableService: UserTableService,
    private route: ActivatedRoute,
    private router: Router,
    private loader: LoadingService,
    private partnerService: PartnerService,
    private companyService: CompanyService,
    private userService: UserService,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
    private questionsService: CreatePartnerService,
    private viewAsService: ViewAsService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
    this.operation = Operation.update;
  }

  // #region Lifecycle Methods

  ngOnInit(): void {
    this.partnerId = this.route.snapshot.paramMap.get("id");
    this.getPartner();
  }

  // #endregion

  /** Populate form data */
  getPartner = (resetForm?: boolean): void => {
    this.partnerService
      .getPartner(this.partnerId)
      .subscribe(
        (partner) => {
          this.partner = partner;
          this.buildForm(this.partner);
          this.setTabs();
          if (resetForm) {
            this.dynamicForm.resetWithValues(this.questions);
          }
        },
        (error) => {
          this.router.navigate(["/partners"]);
          if (this.currentUser.isCWRepUser()) {
            // a global manager CWRep can create a partner but cannot view it until assigned to him
            this.notificationService.error("You do not have permission to view this Partner");
          } else {
            this.notificationService.error(error || "Couldn't find Partner");
          }
        },
      )
      .add(() => this.loader.hide());
  };

  /** Populate companies table */
  getCompanies = (event: GetDataEvent<Company>): void => {
    const { onError, onResult, queryParams } = event;
    this.companyService
      .getCompanies({
        relations: { partner: true },
        ...queryParams,
        where: { ...queryParams?.where, partnerId: this.partnerId },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  /** Populate users table */
  getUsers = (event: GetDataEvent<User>): void => {
    const { onError, onResult, queryParams } = event;
    let where: QueryParams<User>["where"];
    const isSearchingCompany = queryParams ? Object.keys(queryParams.where).includes("company") : false;
    if (!Array.isArray(queryParams?.where) && isSearchingCompany) {
      const { company } = queryParams?.where;
      where = { company: { partnerId: this.partnerId, name: company["name"] } };
    } else {
      where = [
        { ...queryParams?.where, partnerId: this.partnerId },
        { ...queryParams?.where, company: { partnerId: this.partnerId } },
      ];
    }
    this.userService
      .getUsers({
        relations: { company: { partner: true }, partner: true },
        joinType: "LEFT",
        ...queryParams,
        where,
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  /** Populate CW reps table */
  getCWReps = (event: GetDataEvent<User>): void => {
    const { onError, onResult, queryParams } = event;
    const where: QueryParams<User>["where"] = {
      ...queryParams?.where,
      isCWRep: true,
      partners: { id: { $in: [this.partnerId] } },
    };

    this.userService
      .getUsers({
        ...queryParams,
        where,
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  //#region Validators

  canUpdateCWRep() {
    return allowUpdateEntity(this.partner, this.currentUser) && this.currentUser.isGlobalAdmin();
  }

  //#endregion

  // #region Handlers

  onGoBack = (): void => this.goBack();

  onToggleManagePartner = (): void => this.toggleManagePartner();

  onUpdatePartner = (): void => this.updatePartner();

  onDeletePartner = (): void => this.deletePartner();

  /** @workaround fix an issue when table columns are not displayed properly in the table */
  onTabChange = (): void => this.resetTabs();

  // #endregion

  // #region Private Methods

  private setTabs = (): void => {
    if (!this.isEveryTabCreated) {
      this.setCompaniesTab();
      this.setUsersTab();
      this.setCWRepTab();
      this.isEveryTabCreated = true;
    }
  };

  private setCompaniesTab = (): void => {
    this.companiesTableConfig = this.companyTableService.getCompaniesTableConfig({
      entities: { partner: this.partner },
      hidden: ["partner.name"],
    });
    this.companiesTableConfig = {
      ...this.companiesTableConfig,
      toolBar: {
        ...this.companiesTableConfig.toolBar,
        addItem: {
          function: this.addCompany,
          hidden: this.currentUser.hasRight(["viewer"]),
        },
      },
    };
  };

  private setUsersTab = (): void => {
    this.usersTableConfig = this.userTableService.getUsersTableConfig({
      entities: { partner: this.partner },
      hidden: ["partner"],
    });
  };

  private setCWRepTab = (): void => {
    this.cwRepsTableConfig = this.userTableService.getUsersTableConfig({
      entities: { partner: this.partner },
      actionColumn: { delete: { hidden: true } },
      hidden: ["partner", "company.name", "type", "right", "deleted"],
    });

    this.cwRepsTableConfig = {
      ...this.cwRepsTableConfig,
      actionColumn: [
        ...this.cwRepsTableConfig.actionColumn,
        {
          icon: "link_off",
          text: "Unlink",
          function: this.unlinkCWRep,
          hidden: (user) => !allowDeleteEntity(user, this.currentUser),
        },
      ],
      toolBar: {
        ...this.cwRepsTableConfig.toolBar,
        addItem: {
          function: this.linkCWReps,
        },
      },
    };
  };

  private goBack = (): void => {
    this.location.back();
  };

  private toggleManagePartner = (): void => {
    this.isEdit = !this.isEdit;
  };

  private updatePartner = (): void => {
    const body: any = getUpdatedValues(this.dynamicForm.form, this.partner);
    if (body.logo) {
      body.logo = body.logo.split(",")[1];
    } else {
      delete body.logo;
    }
    this.partnerService
      .updatePartner(this.partner.id, body)
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        async (partner) => {
          this.partner = partner;
          this.notificationService.success("Partner has been updated");
          this.buildForm(this.partner);
          this.dynamicForm.markQuestionAsPristine("logo");
          if (body.logo && this.viewAsService.getViewAs()?.partner) {
            await this.viewAsService.saveInLocalStorage({ partnerId: this.partner.id });
          }
          setTimeout(() => this.toggleManagePartner(), 500); // needed to disabled status
        },
        (err) => {
          const error = `There was an error updating the Partner:\n ${err}`;
          this.notificationService.error(error);
        },
      )
      .add(() => this.loader.hide());
  };

  private deletePartner = (): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "partner",
        data: this.partner,
        url: this.partnerService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.notificationService.success("Partner has been deleted");
        this.router.navigate(["/partners"]);
      }
    });
  };

  private cancelPartnerLogo = (): void => {
    this.buildForm(this.partner);
    this.dynamicForm.form.controls["logo"].reset();
  };

  private deletePartnerLogo = (): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "partner logo",
        data: this.partner,
        overwrittenUrl: `${this.partnerService.basePath}/${this.partner.id}/logo`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.notificationService.success("Partner logo has been deleted");
        this.getPartner(true);
      }
    });
  };

  private buildForm = (partner?: Partner): void => {
    const options = {
      partner,
      ...(this.currentUser.isGlobal() && {
        showIsResellerNeeded: true,
      }),
      ...(this.currentUser.isGlobal() &&
        !this.currentUser.hasRight(["viewer"]) && {
          deletePartnerLogoModal: this.partner.logo ? this.deletePartnerLogo : this.cancelPartnerLogo,
        }),
      ...(this.currentUser.hasRight(["admin", "manager"]) && {
        disablePartnerId: false,
      }),
      operation: this.operation,
    };
    this.questionsService.generateQuestions(options).subscribe((qs) => {
      this.questions = qs;
    });
  };

  private addCompany = (table: DataTableComponent<Company>): void => {
    const questions: QuestionBase<string | boolean>[] = [];
    const options = {
      partnerValue: this.partner.id,
      partnerEdit: false,
      canManageClientPriceEdit: true,
    };

    this.createCompanyService.generateQuestions(options).subscribe((qs) => {
      Object.assign(questions, qs);
    });

    this.dialogRef = this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        title: "Add Company",
        component: {
          clazz: DynamicFormComponent,
          onSubmit: "onSubmit",
          inputs: {
            questions,
            showButtons: false,
          },
          events: [
            {
              eventName: "formSubmit",
              handler: (_dynamicForm: DynamicFormComponent, values: Company) => {
                this._addCompany(values, table);
              },
            },
          ],
        },
      },
    });
  };

  private _addCompany = (company: Company, table: DataTableComponent<Company>): void => {
    if (company.type === "") {
      delete company.type;
    }
    if (company.logo) {
      company.logo = company.logo.split(",")[1];
    } else {
      delete company.logo;
    }
    this.companyService.createCompany(company).subscribe(
      () => {
        this.notificationService.success("Company added");
        this.dialogRef.close();
        table.search();
      },
      (error) => {
        console.error(error);
        this.notificationService.error("Could not add Company:\n" + error);
      },
    );
  };

  private linkCWReps = async (table: DataTableComponent<User>): Promise<void> => {
    const partnerId = this.partnerId;

    // Assign users to the partner (used in the popup table)
    const action = (usersTable: DataTableComponent<User>) => {
      if (usersTable.config.selection.selectedRows.length) {
        this.loader.show();
        const body = { users: usersTable.config.selection.selectedRows.map((partner) => partner.id) };
        this.partnerService
          .linkUsers(partnerId, body)
          .subscribe(
            () => {
              this.notificationService.success("CW representatives linked");
              table.search();
            },
            (error) => this.notificationService.error(`Couldn't link CW representatives to partner: ${error}`),
          )
          .add(() => this.loader.hide());
      }
    };

    this.dialog.open<GenericModalComponent, GenericModalData<{ config: DataTable<User> }>>(GenericModalComponent, {
      width: "1400px",
      panelClass: "custom-dialog-container",
      data: {
        title: `Link CW Representatives to ${this.partner.name}`,
        buttons: { hide: false, submit: { action } },
        component: {
          clazz: DataTableComponent,
          inputs: {
            config: this.userTableService.getUsersTableConfig({
              id: "users_to_link",
              hidden: ["partner", "company.name", "type", "right", "deleted"],
              actionColumn: { hidden: true },
              inlineEdit: { disabled: () => true },
              syncWithQueryParams: false,
              selection: { hidden: false },
            }),
          },
          events: [
            {
              eventName: "getData",
              handler: (_dynamicForm: any, event: GetDataEvent<User>) => {
                return this.userTableService.getAvailableUsersToLinkToPartner(this.partnerId, {
                  ...event,
                });
              },
            },
          ],
        },
      },
    });
  };

  private unlinkCWRep = (user: User, table: DataTableComponent<User>): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "CW Rep.",
        data: user,
        unlink: true,
        fullUrl: `${this.userService.basePath}/${user.id}/partners`,
        body: { partners: [this.partnerId] },
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  private resetTabs = (): void => {
    setTimeout(() => this.tables.forEach((table) => table.resetScrollbar()), 0);
  };

  // #endregion
}
